import {
  DestroyRef,
  Injectable,
  NgZone,
  inject,
  isDevMode,
} from '@angular/core';
import { CallStateStore } from './call-state.store';
import type { AudioDevice } from '../models/audio-device.model';
import type { AvailableAudioDevices } from '../models/available-audio-devices.model';
import type { DeviceState } from '../models/device-state.model';

const STORAGE_KEY_INPUT = 'telephony.audioInput';
const STORAGE_KEY_OUTPUT = 'telephony.audioOutput';
const DEVICE_CHANGE_DEBOUNCE_MS = 150;

const DEFAULT_OUTPUT_DEVICE: AudioDevice = {
  deviceId: 'default',
  label: 'Default Speaker',
  kind: 'audiooutput',
};

type AudioElementWithSinkId = HTMLAudioElement & {
  setSinkId?: (sinkId: string) => Promise<void>;
};

/**
 * Audio device enumeration and selection service.
 * Manages microphone and speaker selection with localStorage persistence.
 */
@Injectable({ providedIn: 'root' })
export class DeviceService {
  private readonly store = inject(CallStateStore);
  private readonly destroyRef = inject(DestroyRef);
  private readonly zone = inject(NgZone);
  private readonly onDeviceChangeListener = () => {
    if (this.deviceChangeDebounceTimer) {
      clearTimeout(this.deviceChangeDebounceTimer);
    }

    this.deviceChangeDebounceTimer = setTimeout(() => {
      this.zone.run(() => void this.onDeviceChange());
    }, DEVICE_CHANGE_DEBOUNCE_MS);
  };

  private initialized = false;
  private initializingPromise: Promise<void> | null = null;
  private refreshPromise: Promise<void> | null = null;
  private activeAudioElement: HTMLAudioElement | null = null;
  private auxiliaryAudioElement: HTMLAudioElement | null = null;
  private warmupAudioStream: MediaStream | null = null;
  private warmupPromise: Promise<void> | null = null;
  private deviceChangeDebounceTimer: ReturnType<typeof setTimeout> | null = null;

  constructor() {
    this.destroyRef.onDestroy(() => {
      this.releaseWarmupAudioStream();

      if (this.deviceChangeDebounceTimer) {
        clearTimeout(this.deviceChangeDebounceTimer);
        this.deviceChangeDebounceTimer = null;
      }

      this.activeAudioElement = null;
      this.auxiliaryAudioElement = null;

      if (!this.supportsMediaDevices()) {
        return;
      }

      navigator.mediaDevices.removeEventListener(
        'devicechange',
        this.onDeviceChangeListener
      );
    });
  }

  async getCallMediaStream(
    constraints: MediaStreamConstraints
  ): Promise<MediaStream> {
    if (!this.supportsMediaDevices() || !navigator.mediaDevices?.getUserMedia) {
      throw new Error('Media devices are not available');
    }

    const wantsAudio = constraints.audio !== false;
    const wantsVideo = constraints.video === true;

    if (!wantsAudio && !wantsVideo) {
      return new MediaStream();
    }

    // For audio-only telephony flows, reuse a pre-warmed audio stream.
    if (wantsAudio && !wantsVideo) {
      const selectedInputId = this.getSelectedAudioInputId() ?? undefined;
      await this.ensureWarmupAudioStream(selectedInputId);

      const warmedClone = this.cloneWarmupAudioStream(selectedInputId);
      if (warmedClone) {
        return warmedClone;
      }
    }

    const freshStream = await navigator.mediaDevices.getUserMedia(constraints);

    if (wantsAudio && !wantsVideo) {
      this.replaceWarmupAudioStream(freshStream);

      const clonedFresh = this.cloneWarmupAudioStream(
        this.getSelectedAudioInputId() ?? undefined
      );
      if (clonedFresh) {
        return clonedFresh;
      }
    }

    return freshStream;
  }

  async initialize(): Promise<void> {
    if (this.initialized) {
      await this.refreshDevices();
      return;
    }

    if (this.initializingPromise) {
      await this.initializingPromise;
      return;
    }

    this.initializingPromise = this.initializeInternal();

    try {
      await this.initializingPromise;
      this.initialized = true;
    } finally {
      this.initializingPromise = null;
    }
  }

  getSelectedAudioInputId(): string | null {
    return this.store.deviceState().selectedAudioInputId;
  }

  getSelectedAudioOutputId(): string | null {
    return this.store.deviceState().selectedAudioOutputId;
  }

  setActiveAudioElement(element: HTMLAudioElement | null): void {
    this.activeAudioElement = element;

    const selectedOutputId = this.getSelectedAudioOutputId();
    if (selectedOutputId) {
      void this.applySinkId(selectedOutputId, this.activeAudioElement);
    }
  }

  setAuxiliaryAudioElement(element: HTMLAudioElement | null): void {
    this.auxiliaryAudioElement = element;

    const selectedOutputId = this.getSelectedAudioOutputId();
    if (selectedOutputId) {
      void this.applyOutputToKnownElements(selectedOutputId);
    }
  }

  selectAudioInput(deviceId: string): void {
    if (this.getSelectedAudioInputId() !== deviceId) {
      this.store.setDeviceState({ selectedAudioInputId: deviceId });
      this.persistSelection(STORAGE_KEY_INPUT, deviceId);
    }

    void this.ensureWarmupAudioStream(deviceId).catch(err => {
      this.logWarn(`Audio input warmup failed: ${String(err)}`);
    });
  }

  async selectAudioOutput(deviceId: string): Promise<void> {
    if (this.getSelectedAudioOutputId() !== deviceId) {
      this.store.setDeviceState({ selectedAudioOutputId: deviceId });
      this.persistSelection(STORAGE_KEY_OUTPUT, deviceId);
    }

    await this.applyOutputToKnownElements(deviceId);
  }

  async refreshDevices(): Promise<void> {
    if (!this.supportsMediaDevices()) {
      return;
    }

    if (this.refreshPromise) {
      await this.refreshPromise;
      return;
    }

    this.refreshPromise = this.refreshDevicesInternal();
    try {
      await this.refreshPromise;
    } finally {
      this.refreshPromise = null;
    }
  }

  sinkIdSupported(): boolean {
    return (
      typeof HTMLAudioElement !== 'undefined' &&
      'setSinkId' in HTMLAudioElement.prototype
    );
  }

  private async applySinkId(
    deviceId: string,
    element: HTMLAudioElement | null
  ): Promise<void> {
    const audioWithSink = element as AudioElementWithSinkId | null;
    if (
      !audioWithSink ||
      !this.sinkIdSupported() ||
      typeof audioWithSink.setSinkId !== 'function'
    ) {
      return;
    }

    try {
      await audioWithSink.setSinkId(deviceId);
    } catch (err) {
      this.logWarn(`Failed to set audio output device: ${String(err)}`);
    }
  }

  private async initializeInternal(): Promise<void> {
    if (!this.supportsMediaDevices()) {
      this.store.setError(
        'Audio device APIs are not supported in this browser'
      );
      return;
    }

    await this.ensureMicrophonePermission();
    await this.refreshDevicesInternal();

    this.zone.runOutsideAngular(() => {
      navigator.mediaDevices.removeEventListener(
        'devicechange',
        this.onDeviceChangeListener
      );
      navigator.mediaDevices.addEventListener(
        'devicechange',
        this.onDeviceChangeListener
      );
    });
  }

  private async ensureMicrophonePermission(): Promise<void> {
    if (!this.supportsMediaDevices()) {
      return;
    }

    try {
      // Acquire (and keep) a warm audio stream so call setup avoids cold gUM latency.
      await this.ensureWarmupAudioStream(
        this.store.deviceState().selectedAudioInputId ?? undefined
      );
    } catch (err) {
      this.logWarn(
        `Microphone permission not granted, continuing with limited device info: ${String(
          err
        )}`
      );
    }
  }

  private async readAvailableDevices(): Promise<AvailableAudioDevices | null> {
    if (!this.supportsMediaDevices()) {
      return null;
    }

    let devices: MediaDeviceInfo[] = [];
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
    } catch (err) {
      this.logWarn(`Device enumeration failed: ${String(err)}`);
      return null;
    }

    const audioInputs = this.toAudioDevices(
      devices,
      'audioinput',
      'Microphone'
    );
    const audioOutputs = this.toAudioDevices(devices, 'audiooutput', 'Speaker');

    return {
      audioInputs,
      audioOutputs:
        audioOutputs.length > 0 || !this.sinkIdSupported()
          ? audioOutputs
          : [DEFAULT_OUTPUT_DEVICE],
    };
  }

  private toAudioDevices(
    devices: readonly MediaDeviceInfo[],
    kind: 'audioinput' | 'audiooutput',
    fallbackLabelPrefix: string
  ): AudioDevice[] {
    return devices
      .filter(device => device.kind === kind && device.deviceId)
      .map(device => ({
        deviceId: device.deviceId,
        label:
          device.label || `${fallbackLabelPrefix} ${device.deviceId.slice(0, 5)}`,
        kind,
      }));
  }

  private async onDeviceChange(): Promise<void> {
    try {
      await this.refreshDevices();
    } catch (err) {
      this.logWarn(`Device change handling failed: ${String(err)}`);
    }
  }

  private async ensureWarmupAudioStream(
    inputDeviceId?: string
  ): Promise<void> {
    if (!this.supportsMediaDevices() || !navigator.mediaDevices?.getUserMedia) {
      return;
    }

    if (this.warmupPromise) {
      await this.warmupPromise;
      if (this.hasReusableWarmupAudioStream(inputDeviceId)) {
        return;
      }
    }

    if (this.hasReusableWarmupAudioStream(inputDeviceId)) {
      return;
    }

    const constraints: MediaStreamConstraints = {
      audio: inputDeviceId
        ? { deviceId: { ideal: inputDeviceId } }
        : true,
      video: false,
    };

    this.warmupPromise = navigator.mediaDevices
      .getUserMedia(constraints)
      .then(stream => {
        this.replaceWarmupAudioStream(stream);
      })
      .catch(err => {
        this.logWarn(`Warmup stream acquisition failed: ${String(err)}`);
      })
      .finally(() => {
        this.warmupPromise = null;
      });

    await this.warmupPromise;
  }

  private cloneWarmupAudioStream(
    inputDeviceId?: string
  ): MediaStream | null {
    const sourceTrack = this.getReusableWarmupTrack(inputDeviceId);
    if (!sourceTrack) {
      return null;
    }

    const clonedTrack = sourceTrack.clone();
    return new MediaStream([clonedTrack]);
  }

  private hasReusableWarmupAudioStream(inputDeviceId?: string): boolean {
    return !!this.getReusableWarmupTrack(inputDeviceId);
  }

  private getReusableWarmupTrack(
    inputDeviceId?: string
  ): MediaStreamTrack | null {
    if (!this.warmupAudioStream) {
      return null;
    }

    const sourceTrack = this.warmupAudioStream
      .getAudioTracks()
      .find(track => track.readyState === 'live');

    if (!sourceTrack) {
      return null;
    }

    const trackDeviceId = sourceTrack.getSettings().deviceId;
    if (inputDeviceId && trackDeviceId && trackDeviceId !== inputDeviceId) {
      return null;
    }

    return sourceTrack;
  }

  private replaceWarmupAudioStream(stream: MediaStream): void {
    this.releaseWarmupAudioStream();
    this.warmupAudioStream = stream;
  }

  private releaseWarmupAudioStream(): void {
    if (!this.warmupAudioStream) {
      return;
    }

    this.warmupAudioStream.getTracks().forEach(track => track.stop());
    this.warmupAudioStream = null;
  }

  private readPersistedSelection(key: string): string | null {
    try {
      return localStorage.getItem(key);
    } catch {
      return null;
    }
  }

  private persistSelection(key: string, deviceId: string): void {
    try {
      if (localStorage.getItem(key) === deviceId) {
        return;
      }

      localStorage.setItem(key, deviceId);
    } catch {
      // Ignore storage errors
    }
  }

  private supportsMediaDevices(): boolean {
    return (
      typeof navigator !== 'undefined' &&
      !!navigator.mediaDevices &&
      typeof navigator.mediaDevices.enumerateDevices === 'function'
    );
  }

  private async refreshDevicesInternal(): Promise<void> {
    const currentState = this.store.deviceState();
    const availableDevices = await this.readAvailableDevices();
    const resolvedState = availableDevices
      ? this.resolveDeviceState(currentState, availableDevices)
      : currentState;

    if (availableDevices) {
      this.commitResolvedDeviceState(currentState, resolvedState);

      if (resolvedState.selectedAudioInputId) {
        this.persistSelection(STORAGE_KEY_INPUT, resolvedState.selectedAudioInputId);
      }

      if (resolvedState.selectedAudioOutputId) {
        this.persistSelection(
          STORAGE_KEY_OUTPUT,
          resolvedState.selectedAudioOutputId
        );
      }
    }

    await this.applyCurrentAudioOutputDevice(resolvedState.selectedAudioOutputId);
    await this.ensureWarmupAudioStream(
      resolvedState.selectedAudioInputId ?? undefined
    );
  }

  private resolveDeviceState(
    currentState: DeviceState,
    availableDevices: AvailableAudioDevices
  ): DeviceState {
    const storedInputId = this.readPersistedSelection(STORAGE_KEY_INPUT);
    const storedOutputId = this.readPersistedSelection(STORAGE_KEY_OUTPUT);

    const selectedAudioInputId = this.resolveSelectedDeviceId(
      storedInputId,
      currentState.selectedAudioInputId,
      availableDevices.audioInputs
    );
    const selectedAudioOutputId = this.resolveSelectedDeviceId(
      storedOutputId,
      currentState.selectedAudioOutputId,
      availableDevices.audioOutputs
    );

    return {
      audioInputs: availableDevices.audioInputs,
      audioOutputs: availableDevices.audioOutputs,
      selectedAudioInputId,
      selectedAudioOutputId,
    };
  }

  private resolveSelectedDeviceId(
    storedDeviceId: string | null,
    currentDeviceId: string | null,
    devices: readonly AudioDevice[]
  ): string | null {
    const preferredDeviceId =
      storedDeviceId && devices.some(device => device.deviceId === storedDeviceId)
        ? storedDeviceId
        : currentDeviceId;

    return this.resolveAvailableDeviceId(preferredDeviceId, devices);
  }

  private commitResolvedDeviceState(
    currentState: DeviceState,
    nextState: DeviceState
  ): void {
    if (!this.hasDeviceStateChanged(currentState, nextState)) {
      return;
    }

    this.store.setDeviceState({
      audioInputs: nextState.audioInputs,
      audioOutputs: nextState.audioOutputs,
      selectedAudioInputId: nextState.selectedAudioInputId,
      selectedAudioOutputId: nextState.selectedAudioOutputId,
    });
  }

  private hasDeviceStateChanged(
    currentState: DeviceState,
    nextState: DeviceState
  ): boolean {
    return (
      !this.areAudioDeviceListsEqual(
        currentState.audioInputs,
        nextState.audioInputs
      ) ||
      !this.areAudioDeviceListsEqual(
        currentState.audioOutputs,
        nextState.audioOutputs
      ) ||
      currentState.selectedAudioInputId !== nextState.selectedAudioInputId ||
      currentState.selectedAudioOutputId !== nextState.selectedAudioOutputId
    );
  }

  private areAudioDeviceListsEqual(
    first: readonly AudioDevice[],
    second: readonly AudioDevice[]
  ): boolean {
    if (first.length !== second.length) {
      return false;
    }

    return first.every((device, index) => {
      const nextDevice = second[index];
      return (
        !!nextDevice &&
        device.deviceId === nextDevice.deviceId &&
        device.label === nextDevice.label &&
        device.kind === nextDevice.kind
      );
    });
  }

  private async applyCurrentAudioOutputDevice(
    selectedOutputId = this.store.deviceState().selectedAudioOutputId
  ): Promise<void> {
    if (!selectedOutputId) {
      return;
    }

    await this.applyOutputToKnownElements(selectedOutputId);
  }

  private async applyOutputToKnownElements(deviceId: string): Promise<void> {
    const outputTargets = this.getOutputTargets();
    if (outputTargets.length === 0) {
      return;
    }

    await Promise.all(
      outputTargets.map(target => this.applySinkId(deviceId, target))
    );
  }

  private getOutputTargets(): HTMLAudioElement[] {
    const targets: HTMLAudioElement[] = [];

    if (this.activeAudioElement) {
      targets.push(this.activeAudioElement);
    }

    if (
      this.auxiliaryAudioElement &&
      this.auxiliaryAudioElement !== this.activeAudioElement
    ) {
      targets.push(this.auxiliaryAudioElement);
    }

    return targets;
  }

  private resolveAvailableDeviceId(
    preferredDeviceId: string | null,
    devices: readonly AudioDevice[]
  ): string | null {
    if (devices.length === 0) {
      return null;
    }

    if (
      preferredDeviceId &&
      devices.some(device => device.deviceId === preferredDeviceId)
    ) {
      return preferredDeviceId;
    }

    return devices[0].deviceId;
  }

  private logWarn(message: string): void {
    if (isDevMode()) {
      console.warn(`[DeviceService] ${message}`);
    }
  }
}
